import {
  patchWorkoutPreferences,
  syncWorkoutExecution,
} from "../api/workoutLogApi";
import { isRetryableSyncError } from "../domain/syncIssue";
import { isUnmappedPlaceholder } from "../domain/substitution";
import { workoutLogStore } from "../store/localExecutionStore";
import { SyncScheduler } from "./syncScheduler";

const QUARANTINE_CODES = new Set([
  "LEASE_LOST",
  "EXECUTION_FINALIZED",
  "EXECUTION_DISCARDED",
]);

const ITEM_FIELDS = [
  "itemId", "position", "blockKey", "laneIndex", "origin",
  "prescribedExerciseKey", "substitutedFromExerciseId", "exerciseId",
  "notes", "skipped", "cardio", "sets",
];
const SET_FIELDS = [
  "setId", "setIndex", "origin", "prescribedSetKey", "setType", "load",
  "reps", "seconds", "rir", "performedAt", "lastEditedAt", "enteredAfterFinish",
];
const DOCUMENT_FIELDS = [
  "status", "finishedAt", "workoutEndedAt", "finishTimezone", "savedAt",
  "finalizedBy", "discardedAt", "notes",
];

function pick(value, fields) {
  return Object.fromEntries(fields.map((field) => [field, value?.[field] ?? null]));
}

export function buildSyncDocument(record) {
  const document = pick(record.document, DOCUMENT_FIELDS);
  return {
    ...document,
    lastActivityAt: record.timing.lastActivityAt,
    activeSegments: record.timing.activeSegments,
    // An unmapped slot's local placeholder is never sent: the server accepts
    // the slot empty until an exercise is chosen (§15.8). Profiles are never
    // client-writable (§12.1).
    items: (record.document.items || [])
      .filter((item) => !isUnmappedPlaceholder(record, item))
      .map((item) => ({
        ...pick(item, ITEM_FIELDS.filter((field) => !["cardio", "sets"].includes(field))),
        cardio: item.cardio == null ? null : pick(item.cardio, ["minutes", "completed", "completedAt"]),
        sets: (item.sets || []).map((set) => pick(set, SET_FIELDS)),
      })),
  };
}

export function buildSyncPayload(record) {
  return {
    clientSchemaVersion: 1,
    deviceId: record.deviceId,
    leaseEpoch: record.leaseEpoch,
    clientRevision: record.localRevision,
    document: buildSyncDocument(record),
  };
}

function parseRetryAfter(value, now = Date.now()) {
  if (value == null || value === "") return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = new Date(value).getTime();
  return Number.isFinite(date) ? Math.max(0, date - now) : null;
}

export class WorkoutSyncEngine {
  constructor({
    store = workoutLogStore,
    syncApi = syncWorkoutExecution,
    preferencesApi = patchWorkoutPreferences,
    getBootstrap = null,
    scheduler = null,
    navigatorObject = typeof navigator === "undefined" ? null : navigator,
  } = {}) {
    this.store = store;
    this.syncApi = syncApi;
    this.preferencesApi = preferencesApi;
    this.getBootstrap = getBootstrap;
    this.bootstrap = { status: "resolving", user: null };
    this.navigatorObject = navigatorObject;
    this.inFlight = new Map();
    this.listeners = new Set();
    this.scheduler = scheduler || new SyncScheduler({
      onTrigger: ({ reason, ...context }) => this.syncPending({ reason, ...context }),
    });
  }

  start() {
    this.scheduler.start();
  }

  stop() {
    this.scheduler.stop();
  }

  currentBootstrap() {
    return this.getBootstrap ? this.getBootstrap() : this.bootstrap;
  }

  setBootstrap(bootstrap) {
    const previousUser = this.currentBootstrap()?.user?.id;
    this.bootstrap = bootstrap || { status: "anonymous", user: null };
    const nextUser = this.bootstrap?.user?.id;
    if (this.bootstrap.status === "authenticated" && nextUser) {
      this.scheduler.trigger(
        previousUser === nextUser ? "authenticated-bootstrap" : "auth-changed",
        { userId: nextUser }
      );
    }
    this.emit();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(event = null) {
    this.listeners.forEach((listener) => listener(event));
  }

  trigger(reason, context = {}) {
    this.scheduler.trigger(reason, context);
  }

  async getStatus(userId) {
    if (!userId) {
      return { pendingCount: 0, states: [], lastError: null, syncingExecutionIds: [] };
    }
    const records = await this.store.listExecutions(userId);
    const pending = records.filter((record) => record.localRevision > record.ackedRevision);
    return {
      pendingCount: pending.length,
      states: [...new Set(records.map((record) => record.syncState))],
      lastError: records.find((record) => record.lastSyncError)?.lastSyncError || null,
      // Executions with a push request actually in flight right now.
      syncingExecutionIds: [...this.inFlight.keys()],
    };
  }

  isEligible(userId) {
    const bootstrap = this.currentBootstrap();
    return bootstrap?.status === "authenticated" && bootstrap.user?.id === userId;
  }

  isOnline() {
    return this.navigatorObject?.onLine !== false;
  }

  async syncNow(userId = this.currentBootstrap()?.user?.id) {
    this.scheduler.resetBackoff();
    return this.syncPending({ reason: "sync-now", userId });
  }

  async syncPending({ userId = this.currentBootstrap()?.user?.id } = {}) {
    if (!userId || !this.isEligible(userId) || !this.isOnline()) return [];
    const records = await this.store.getPendingExecutions(userId);
    const results = await Promise.all(records.map((record) =>
      this.syncExecution(userId, record.executionId)
    ));
    await this.flushPreferences(userId);
    this.emit();
    return results;
  }

  syncExecution(userId, executionId) {
    if (!this.isEligible(userId)) return Promise.resolve(null);
    if (this.inFlight.has(executionId)) return this.inFlight.get(executionId);
    const request = this.runExecutionLoop(userId, executionId)
      .finally(() => {
        this.inFlight.delete(executionId);
        this.emit();
      });
    this.inFlight.set(executionId, request);
    // Status-only event: nothing in the store changed, but "Syncing" is now true.
    this.emit({ type: "SYNC_ACTIVITY", executionId });
    return request;
  }

  async runExecutionLoop(userId, executionId) {
    while (this.isEligible(userId) && this.isOnline()) {
      const record = await this.store.getExecution(userId, executionId);
      if (!record || record.userId !== userId || record.localRevision <= record.ackedRevision) {
        return record;
      }
      const sentRevision = record.localRevision;
      const payload = buildSyncPayload(record);
      const sentRecord = {
        ...record,
        localRevision: payload.clientRevision,
        document: payload.document,
      };
      try {
        const response = await this.syncApi(executionId, payload);
        const updated = await this.store.updateExecution(userId, executionId, (current) => {
          const responseAck = Number(response?.ack);
          // An ACK names what the server accepted from this lease: it applies
          // only to the same lease epoch at or after the sent revision, never
          // to a different document that replaced it meanwhile.
          const sameLineage =
            Number(current.leaseEpoch || 0) === Number(sentRecord.leaseEpoch || 0) &&
            Number(current.localRevision || 0) >= sentRevision;
          const ackAppliesToSentRevision = sameLineage &&
            Number.isSafeInteger(responseAck) &&
            responseAck >= 0 && responseAck <= sentRevision;
          const safeAck = ackAppliesToSentRevision
            ? Math.min(responseAck, current.localRevision)
            : current.ackedRevision;
          const ackedRevision = Math.max(current.ackedRevision, safeAck);
          const pending = current.localRevision > ackedRevision;
          const result = ackAppliesToSentRevision && response?.status === "SAVED" ? {
            outcome: response.outcome ?? null,
            extraReason: response.extraReason ?? null,
            satisfiedOccurrence: response.satisfiedOccurrence ?? null,
            serverSavedAt: response.serverSavedAt ?? null,
          } : current.result;
          return {
            ...current,
            ackedRevision,
            pending,
            syncState: pending ? "PENDING" : "SYNCED",
            result,
            lastAcknowledgedAt: ackAppliesToSentRevision
              ? new Date().toISOString()
              : current.lastAcknowledgedAt,
            lastSyncError: ackAppliesToSentRevision ? null : current.lastSyncError,
          };
        });
        this.scheduler.resetBackoff();
        // Typed so the auth owner can tell a push the server accepted for this
        // user (its AUTH_REQUIRED episode is over) from any other change.
        this.emit({ type: "SYNC_ACCEPTED", userId, executionId });
        if (!updated || updated.localRevision <= updated.ackedRevision) return updated;
        if (updated.localRevision <= sentRevision) return updated;
      } catch (error) {
        return this.handleSyncError(userId, sentRecord, error);
      }
    }
    return null;
  }

  async handleSyncError(userId, sentRecord, error) {
    const code = error?.code || "SYNC_FAILED";
    if (error?.status === 401) {
      // The auth owner re-resolves the Session once per episode (§11.3); the
      // engine itself never retries or redirects.
      return this.markState(userId, sentRecord.executionId, "AUTH_REQUIRED", error, {
        type: "AUTH_REQUIRED",
        userId,
        executionId: sentRecord.executionId,
      });
    }
    if (error?.status === 426 || code === "CLIENT_SCHEMA_UNSUPPORTED") {
      return this.markState(userId, sentRecord.executionId, "UPGRADE_REQUIRED", error);
    }
    if (error?.status === 409 && QUARANTINE_CODES.has(code)) {
      if (error.details?.quarantined === true) {
        const result = await this.store.confirmServerQuarantine(sentRecord, code);
        if (result.invalidatedCurrent) {
          this.emit({
            type: "EXECUTION_QUARANTINED",
            userId,
            executionId: sentRecord.executionId,
            code,
          });
        }
        return result.record;
      }
      await this.store.preserveRejectedExecution(sentRecord, code);
      return this.store.getExecution(userId, sentRecord.executionId);
    }
    const retryable = isRetryableSyncError(error);
    const updated = await this.markState(userId, sentRecord.executionId, "ERROR", error);
    if (retryable) {
      this.scheduler.scheduleRetry(
        error.status === 429 ? parseRetryAfter(error.retryAfter) : null,
        { userId }
      );
    }
    return updated;
  }

  async markState(userId, executionId, syncState, error, event = null) {
    const updated = await this.store.updateExecution(userId, executionId, (current) => ({
      ...current,
      syncState,
      lastSyncError: {
        code: error?.code || "SYNC_FAILED",
        status: error?.status || null,
        message: error?.message || "Workout sync failed.",
      },
    }));
    this.emit(event);
    return updated;
  }

  async flushPreferences(userId) {
    if (!this.isEligible(userId) || !this.isOnline()) return null;
    const cached = await this.store.getPreferences(userId);
    if (!cached?.pendingPatch) return cached;
    const revision = cached.localRevision;
    try {
      const preferences = await this.preferencesApi(cached.pendingPatch);
      const acknowledged = await this.store.acknowledgePreferences(
        userId,
        revision,
        preferences
      );
      this.emit();
      return acknowledged;
    } catch (error) {
      if (!error?.status || error.status >= 500 || error.status === 429) {
        this.scheduler.scheduleRetry(
          error.status === 429 ? parseRetryAfter(error.retryAfter) : null,
          { userId }
        );
      }
      return cached;
    }
  }
}

export const workoutSyncEngine = new WorkoutSyncEngine();

export { parseRetryAfter };
